/**
 * Utilitários para manipulação de datas no frontend
 * Corrige problemas de timezone para exibição correta
 */
export class DateUtils {
  /**
   * Formata uma data do formato ISO (YYYY-MM-DD) para exibição brasileira (DD/MM/YYYY)
   * Corrige problemas de timezone garantindo que a data seja interpretada corretamente
   */
  static formatDateBR(dateString: string): string {
    // Se a data já contém horário, usar diretamente
    if (dateString.includes('T')) {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo'
      });
    }
    
    // ✅ CORREÇÃO: Para datas no formato YYYY-MM-DD, adicionar horário para evitar problemas de timezone
    const dateWithTime = `${dateString}T12:00:00-03:00`;
    return new Date(dateWithTime).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    });
  }

  /**
   * Formata uma data para exibição compacta (DD/MM)
   */
  static formatDateCompact(dateString: string): string {
    if (dateString.includes('T')) {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'America/Sao_Paulo'
      });
    }
    
    const dateWithTime = `${dateString}T12:00:00-03:00`;
    return new Date(dateWithTime).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });
  }

  /**
   * Converte data para input HTML (YYYY-MM-DD)
   */
  static formatForInput(dateString: string): string {
    if (dateString.includes('T')) {
      return new Date(dateString).toISOString().split('T')[0];
    }
    
    // Para datas já no formato YYYY-MM-DD, retornar como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Para outros formatos, tentar converter
    const dateWithTime = `${dateString}T12:00:00-03:00`;
    return new Date(dateWithTime).toISOString().split('T')[0];
  }

  /**
   * Verifica se uma data é válida
   */
  static isValidDate(dateString: string): boolean {
    if (!dateString) return false;
    
    try {
      const date = dateString.includes('T') 
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      return !isNaN(date.getTime());
    } catch {
      return false;
    }
  }

  /**
   * Verifica se uma data é no passado
   */
  static isDatePast(dateString: string): boolean {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const compareDate = dateString.includes('T')
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      compareDate.setHours(0, 0, 0, 0);
      return compareDate < today;
    } catch {
      return false;
    }
  }

  /**
   * Verifica se uma data é no futuro
   */
  static isDateFuture(dateString: string): boolean {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const compareDate = dateString.includes('T')
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      compareDate.setHours(0, 0, 0, 0);
      return compareDate > today;
    } catch {
      return false;
    }
  }

  /**
   * Verifica se uma data é hoje
   */
  static isDateToday(dateString: string): boolean {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const compareDate = dateString.includes('T')
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      compareDate.setHours(0, 0, 0, 0);
      return compareDate.getTime() === today.getTime();
    } catch {
      return false;
    }
  }

  /**
   * Obter nome do dia da semana
   */
  static getDayName(dateString: string): string {
    try {
      const date = dateString.includes('T')
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      return date.toLocaleDateString('pt-BR', { 
        weekday: 'long',
        timeZone: 'America/Sao_Paulo'
      });
    } catch {
      return '';
    }
  }

  /**
   * Obter nome do mês
   */
  static getMonthName(dateString: string): string {
    try {
      const date = dateString.includes('T')
        ? new Date(dateString)
        : new Date(`${dateString}T12:00:00-03:00`);
      
      return date.toLocaleDateString('pt-BR', { 
        month: 'long',
        timeZone: 'America/Sao_Paulo'
      });
    } catch {
      return '';
    }
  }
} 